<section class="branding-page create-brand-page">
  <!-- Top Bar: Standard 76px edge-to-edge header matching Manage Brands Figma -->
  <header class="page-topbar hero-section branding-topbar create-brand-topbar">
    <div class="topbar-title-block page-header__title">
      <h1 class="page-title">Manage Brands</h1>
      <p class="page-subtitle helper-copy">
        Customize emails and document signing pages with your brand colors, logos, and more.
      </p>
    </div>

    <div class="page-header__actions">
      <button type="button" class="btn btn-secondary ghost-btn" (click)="goBack()">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary primary-btn btn-purple"
        (click)="saveBrand()"
        [disabled]="saving"
      >
        @if (saving) {
          <span class="btn-spinner" aria-hidden="true"></span>
          <span>Saving...</span>
        } @else {
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="10" y1="4" x2="10" y2="16"></line>
            <line x1="4" y1="10" x2="16" y2="10"></line>
          </svg>
          <span>Create New Brand</span>
        }
      </button>
    </div>
  </header>

  <!-- Page Body -->
  <div class="branding-body create-brand-body">
    <!-- Subheader Row: Back Button + Title matching Figma -->
    <div class="create-brand-subnav-row">
      <button
        type="button"
        class="create-brand-back-btn"
        (click)="goBack()"
        aria-label="Back to branding"
        title="Back to branding"
      >
        <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="16" y1="10" x2="4" y2="10"></line>
          <polyline points="10 4 4 10 10 16"></polyline>
        </svg>
      </button>
      <h2 class="create-brand-subheading">Create New Brand</h2>
    </div>

    <!-- Segmented Tab Strip matching Figma -->
    <div class="tab-list create-brand-tab-strip" role="tablist" aria-label="Create brand tabs">
      <button
        type="button"
        class="tab-btn brand-tab-btn"
        role="tab"
        [attr.aria-selected]="activeTab === 'general'"
        [class.is-active]="activeTab === 'general'"
        (click)="selectTab('general')"
      >
        General
      </button>
      <button
        type="button"
        class="tab-btn brand-tab-btn"
        role="tab"
        [attr.aria-selected]="activeTab === 'advanced'"
        [class.is-active]="activeTab === 'advanced'"
        (click)="selectTab('advanced')"
      >
        Advanced
      </button>
      <button
        type="button"
        class="tab-btn brand-tab-btn is-disabled"
        role="tab"
        aria-disabled="true"
      >
        Email Content Customization
      </button>
    </div>

    <!-- Tab 1: General (Form + Sticky Live Preview) -->
    @if (activeTab === 'general') {
      <div class="create-brand-grid-layout">
        <!-- Left Column: Form Sections Card -->
        <main class="create-brand-form-card" aria-label="Create brand form">
          <!-- Section 1: Brand Identity -->
          <section class="form-section form-section--identity">
            <div class="section-title-wrap">
              <h3 class="form-section-title">Brand Identity</h3>
              <p class="form-section-desc">Set your brand name and make it the default for all documents.</p>
            </div>

            <div class="field-wrap">
              <label class="field-label" for="brandName">Brand Name</label>
              <input
                id="brandName"
                type="text"
                class="brand-input"
                [(ngModel)]="draft.name"
                (ngModelChange)="nameErrorMessage = ''"
                placeholder="Enter Brand Name"
              />
              @if (nameErrorMessage) {
                <p class="field-error" role="alert">{{ nameErrorMessage }}</p>
              }
            </div>

            <label class="custom-checkbox-row">
              <input type="checkbox" [(ngModel)]="draft.isDefault" class="brand-checkbox" />
              <div class="checkbox-copy">
                <span class="checkbox-title">Set as default</span>
                <span class="checkbox-desc">Use this brand for all new documents automatically</span>
              </div>
            </label>
          </section>

          <hr class="form-divider" />

          <!-- Section 2: Logo Upload Dropzone -->
          <section class="form-section form-section--logo">
            <div class="section-title-wrap">
              <h3 class="form-section-title">Logo</h3>
              <p class="form-section-desc">
                Upload your logo by selecting a file from your computer or dragging and dropping it into the box shown
                below. The recommended logo size is <strong>320 x 50 pixels</strong>.
              </p>
            </div>

            <input
              #logoInput
              type="file"
              class="hidden-file sr-only"
              accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
              (change)="onLogoSelected($event)"
            />

            <div
              class="logo-dropzone"
              [class.is-drag-active]="dragActive"
              [class.has-preview]="hasLogoPreview"
              (dragover)="onLogoDragOver($event)"
              (dragleave)="onLogoDragLeave($event)"
              (drop)="onLogoDrop($event)"
            >
              @if (hasLogoPreview) {
                <div class="logo-preview-card">
                  <div class="logo-preview-image-wrap">
                    <img [src]="draft.logoDataUrl" [alt]="draft.name || 'Brand logo preview'" />
                  </div>
                  <div class="logo-preview-details">
                    <p class="logo-preview-filename">{{ draft.logoFileName || 'Selected logo' }}</p>
                    <button type="button" class="btn-remove-logo" (click)="clearLogo()">
                      <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <line x1="15" y1="5" x2="5" y2="15"></line>
                        <line x1="5" y1="5" x2="15" y2="15"></line>
                      </svg>
                      <span>Remove logo</span>
                    </button>
                  </div>
                </div>
              } @else {
                <div class="dropzone-empty-content">
                  <div class="dropzone-icon-circle" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  </div>
                  <p class="dropzone-prompt">
                    Drop your logo here, or
                    <button type="button" class="link-btn-browse" (click)="triggerLogoBrowse()">Browse</button>
                  </p>
                  <p class="dropzone-sub">png, jpg or svg up to 5MB</p>
                </div>
              }
            </div>

            @if (logoErrorMessage) {
              <p class="field-error" role="alert">{{ logoErrorMessage }}</p>
            }
          </section>

          <hr class="form-divider" />

          <!-- Section 3: Email & Signer page Customization -->
          <section class="form-section form-section--email">
            <div class="section-title-wrap">
              <h3 class="form-section-title">Email &amp; Signer page Customization</h3>
            </div>

            <div class="field-wrap">
              <label class="field-label" for="emailDisplayName">Email Display Name</label>
              <p class="field-helper-copy">
                You can optionally specify the "From" name that will be shown in emails. The default value uses your brand
                name when no custom display name is provided.
              </p>
              <input
                id="emailDisplayName"
                type="text"
                class="brand-input"
                [(ngModel)]="draft.emailDisplayName"
                placeholder="Enter From Name..."
              />
            </div>
          </section>

          <hr class="form-divider" />

          <!-- Section 4: Brand Colors -->
          <section class="form-section form-section--colors">
            <div class="section-title-wrap">
              <h3 class="form-section-title">Colors</h3>
              <p class="form-section-desc">Choose your brand colors</p>
            </div>

            <div class="colors-stack">
              <!-- Color 1: Email background -->
              <div class="color-picker-row">
                <span class="color-picker-label">Email background color</span>
                <div class="color-control-box">
                  <span class="color-swatch-preview" [style.background]="draft.emailBackgroundColor" aria-hidden="true"></span>
                  <div class="color-input-shell">
                    <input type="color" [(ngModel)]="draft.emailBackgroundColor" aria-label="Email background color" />
                    <span class="color-code-display">{{ draft.emailBackgroundColor }}</span>
                    <span class="color-picker-caret" aria-hidden="true">▾</span>
                  </div>
                </div>
              </div>

              <!-- Colors Grid: Button & Button Text -->
              <div class="colors-two-col">
                <div class="color-picker-row">
                  <span class="color-picker-label">Primary button color</span>
                  <div class="color-control-box">
                    <span class="color-swatch-preview" [style.background]="draft.primaryButtonColor" aria-hidden="true"></span>
                    <div class="color-input-shell">
                      <input type="color" [(ngModel)]="draft.primaryButtonColor" aria-label="Primary button color" />
                      <span class="color-code-display">{{ draft.primaryButtonColor }}</span>
                      <span class="color-picker-caret" aria-hidden="true">▾</span>
                    </div>
                  </div>
                </div>

                <div class="color-picker-row">
                  <span class="color-picker-label">Primary button text color</span>
                  <div class="color-control-box">
                    <span class="color-swatch-preview is-bordered" [style.background]="draft.primaryButtonTextColor" aria-hidden="true"></span>
                    <div class="color-input-shell">
                      <input type="color" [(ngModel)]="draft.primaryButtonTextColor" aria-label="Primary button text color" />
                      <span class="color-code-display">{{ draft.primaryButtonTextColor }}</span>
                      <span class="color-picker-caret" aria-hidden="true">▾</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <hr class="form-divider" />

          <!-- Section 5: Platform Branding Toggle -->
          <section class="form-section form-section--branding">
            <div class="toggle-card-row">
              <label class="toggle-switch" aria-label="Show XlhireEsigns Branding">
                <input type="checkbox" [(ngModel)]="draft.showPlatformBranding" />
                <span class="toggle-switch__track" aria-hidden="true">
                  <span class="toggle-switch__thumb"></span>
                </span>
              </label>
              <div class="toggle-copy">
                <span class="toggle-title">Show XlhireEsigns Branding</span>
                <p class="toggle-desc">Hide the product footer branding from the previewed email and signer page.</p>
              </div>
            </div>
          </section>

          <!-- Form Actions Footer -->
          <div class="form-actions-footer">
            <button type="button" class="btn btn-secondary ghost-btn" (click)="goBack()">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary primary-btn btn-purple"
              (click)="saveBrand()"
              [disabled]="saving"
            >
              @if (saving) {
                <span class="btn-spinner" aria-hidden="true"></span>
                <span>Saving...</span>
              } @else {
                <span>Save</span>
              }
            </button>
          </div>
        </main>

        <!-- Right Column: Sticky Live Preview Panel matching Figma -->
        <aside class="create-brand-preview-card" aria-label="Live brand preview">
          <div class="preview-header-row">
            <h3 class="preview-title">Preview</h3>
          </div>

          <!-- Preview Controls: Mode switch + Device switch -->
          <div class="preview-controls-row">
            <div class="preview-mode-pill-strip" role="tablist" aria-label="Preview view mode">
              <button
                type="button"
                class="preview-pill-btn"
                [class.is-active]="previewMode === 'email'"
                [attr.aria-selected]="previewMode === 'email'"
                (click)="selectPreviewMode('email')"
              >
                Email View
              </button>
              <button
                type="button"
                class="preview-pill-btn"
                [class.is-active]="previewMode === 'signer'"
                [attr.aria-selected]="previewMode === 'signer'"
                (click)="selectPreviewMode('signer')"
              >
                Single Page View
              </button>
            </div>

            <div class="preview-device-toggle" role="tablist" aria-label="Preview device">
              <button
                type="button"
                class="device-icon-btn"
                [class.is-active]="deviceMode === 'desktop'"
                [attr.aria-selected]="deviceMode === 'desktop'"
                (click)="selectDeviceMode('desktop')"
                title="Desktop preview"
                aria-label="Desktop preview"
              >
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="2" y="3" width="16" height="11" rx="2"></rect>
                  <line x1="7" y1="17" x2="13" y2="17"></line>
                  <line x1="10" y1="14" x2="10" y2="17"></line>
                </svg>
              </button>
              <button
                type="button"
                class="device-icon-btn"
                [class.is-active]="deviceMode === 'mobile'"
                [attr.aria-selected]="deviceMode === 'mobile'"
                (click)="selectDeviceMode('mobile')"
                title="Mobile preview"
                aria-label="Mobile preview"
              >
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="5" y="2" width="10" height="16" rx="2"></rect>
                  <line x1="9" y1="15" x2="11" y2="15"></line>
                </svg>
              </button>
            </div>
          </div>

          <!-- Preview Screen Stage with Dynamic Background -->
          <div
            class="preview-stage"
            [class.is-mobile]="deviceMode === 'mobile'"
            [style.background]="draft.emailBackgroundColor"
          >
            @if (previewMode === 'email') {
              <!-- Email View Mock -->
              <div class="email-preview-container">
                <div class="email-preview-header">
                  <div class="email-meta-line">
                    <strong>From:</strong>
                    <span class="meta-from-text">{{ draft.emailDisplayName.trim() || (draft.name.trim() || 'Your Brand') }}</span>
                  </div>
                  <div class="email-meta-line">
                    <strong>To:</strong>
                    <span class="meta-skeleton-bar"></span>
                  </div>
                  <div class="email-meta-line">
                    <strong>CC:</strong>
                    <span class="meta-skeleton-bar meta-skeleton-bar--short"></span>
                  </div>
                </div>

                <div class="email-preview-card-wrap">
                  <div class="email-card-inner">
                    <!-- Brand Header / Logo -->
                    <div class="preview-brand-header">
                      @if (hasLogoPreview) {
                        <img class="preview-logo" [src]="draft.logoDataUrl" [alt]="draft.name || 'Brand logo preview'" />
                      } @else {
                        <div class="preview-logo-placeholder" [style.background]="draft.primaryButtonColor">
                          <span>{{ previewBrandInitials }}</span>
                        </div>
                      }
                      <span class="preview-brand-name">{{ draft.name.trim() || 'Brand Name' }}</span>
                    </div>

                    <p class="preview-body-desc">
                      {{ draft.emailDisplayName.trim() || 'Send documents with your custom branding' }}
                    </p>

                    <!-- Skeleton placeholder lines -->
                    <div class="preview-skeleton-lines" aria-hidden="true">
                      <span class="skeleton-line-full"></span>
                      <span class="skeleton-line-mid"></span>
                      <span class="skeleton-line-short"></span>
                    </div>

                    <!-- Dynamic Action Button -->
                    <button
                      type="button"
                      class="preview-cta"
                      [style.background]="draft.primaryButtonColor"
                      [style.color]="draft.primaryButtonTextColor"
                    >
                      VIEW DOCUMENT
                    </button>

                    <div class="preview-footer-line" aria-hidden="true"></div>

                    @if (draft.showPlatformBranding) {
                      <small class="preview-powered">Powered by Xlhire Esigns</small>
                    }
                  </div>
                </div>
              </div>
            } @else {
              <!-- Signer Page View Mock -->
              <div class="signer-preview-container">
                <div class="signer-header-bar">
                  <div class="preview-brand-header">
                    @if (hasLogoPreview) {
                      <img class="preview-logo" [src]="draft.logoDataUrl" [alt]="draft.name || 'Brand logo preview'" />
                    } @else {
                      <div class="preview-logo-placeholder" [style.background]="draft.primaryButtonColor">
                        <span>{{ previewBrandInitials }}</span>
                      </div>
                    }
                    <span class="preview-brand-name">{{ draft.name.trim() || 'Brand Name' }}</span>
                  </div>
                </div>

                <div class="signer-document-card">
                  <h4 class="signer-doc-title">{{ draft.name.trim() || 'Brand Name' }}</h4>
                  <p class="signer-doc-desc">
                    Review and sign this document with your custom brand theme applied.
                  </p>

                  <div class="preview-skeleton-lines" aria-hidden="true">
                    <span class="skeleton-line-full"></span>
                    <span class="skeleton-line-mid"></span>
                    <span class="skeleton-line-short"></span>
                  </div>

                  <button
                    type="button"
                    class="preview-cta"
                    [style.background]="draft.primaryButtonColor"
                    [style.color]="draft.primaryButtonTextColor"
                  >
                    Start signing
                  </button>
                </div>

                @if (draft.showPlatformBranding) {
                  <small class="preview-powered">Powered by Xlhire Esigns</small>
                }
              </div>
            }
          </div>
        </aside>
      </div>
    } @else if (activeTab === 'advanced') {
      <!-- Tab 2: Advanced (Signature Frame Settings) -->
      <div class="create-brand-advanced-wrap">
        <article class="advanced-settings-card" aria-label="Signature frame settings">
          <header class="advanced-settings-header">
            <div class="toggle-card-row">
              <label class="toggle-switch" aria-label="Add a signature frame">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.enabled" />
                <span class="toggle-switch__track" aria-hidden="true">
                  <span class="toggle-switch__thumb"></span>
                </span>
              </label>
              <div class="toggle-copy">
                <span class="toggle-title">Add a signature frame</span>
                <p class="toggle-desc">Adds a frame around the signature with the selected details.</p>
              </div>
            </div>
          </header>

          @if (previewSignatureFrame.enabled) {
            <hr class="form-divider" />

            <div class="signature-frame-options" role="group" aria-label="Signature frame detail options">
              <label class="custom-checkbox-row">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.showRecipientName" class="brand-checkbox" />
                <span class="checkbox-title">Recipient Name</span>
              </label>
              <label class="custom-checkbox-row">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.showRecipientEmail" class="brand-checkbox" />
                <span class="checkbox-title">Recipient Email</span>
              </label>
              <label class="custom-checkbox-row">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.showTimestamp" class="brand-checkbox" />
                <span class="checkbox-title">Timestamp</span>
              </label>
            </div>

            <div class="signature-frame-preview-card">
              <p class="signature-frame-preview-label">Preview:</p>
              <div class="signature-frame-preview-box">
                <p class="signature-frame-preview__timestamp">
                  XlhireEsigns@if (previewSignatureFrame.showTimestamp) { · Jul 21, 2026, 06:09 PM }
                </p>
                <div class="signature-frame-preview__body">
                  <div class="signature-frame-preview__signature">John Doe</div>
                  @if (previewSignatureFrame.showRecipientName) {
                    <p class="signature-frame-preview__meta">John Doe</p>
                  }
                  @if (previewSignatureFrame.showRecipientEmail) {
                    <p class="signature-frame-preview__meta">johndoe@example.com</p>
                  }
                  <p class="signature-frame-preview__document-id">1fc5d60f-46ca-41b9-a5d6-fad53595bf43</p>
                </div>
              </div>
            </div>
          }

          <!-- Actions Footer inside Advanced -->
          <div class="form-actions-footer">
            <button type="button" class="btn btn-secondary ghost-btn" (click)="goBack()">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary primary-btn btn-purple"
              (click)="saveBrand()"
              [disabled]="saving"
            >
              @if (saving) {
                <span class="btn-spinner" aria-hidden="true"></span>
                <span>Saving...</span>
              } @else {
                <span>Save</span>
              }
            </button>
          </div>
        </article>
      </div>
    }
  </div>
</section>
